// src/pages/admin/TrashedBranchList.jsx
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import DataTable from "../list/DataTable";

import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import Icon from "@mui/material/Icon";
import RestoreIcon from "@mui/icons-material/Restore";

import { API_URL } from "config";
import FlagLoader from "layouts/flagloader";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const TrashedBranchList = () => {
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "success",
  });
  const [restoringId, setRestoringId] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const navigate = useNavigate();

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasBranchPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );

  const canViewPage = hasBranchPermission("branch.pages.trashed.view");
  const canCreate =
    hasBranchPermission("branch.pages.create.view") &&
    hasBranchPermission("branch.actions.open_create");
  const canOpenList =
    hasBranchPermission("branch.pages.list.view") &&
    hasBranchPermission("branch.actions.open_list");
  const canRestore = hasBranchPermission("branch.actions.restore");
  const canConfirmRestore = hasBranchPermission("branch.actions.confirm_restore");
  const canCancelRestore = hasBranchPermission("branch.actions.cancel_restore");
  const canViewField = useCallback(
    (field) => hasBranchPermission(`branch.fields.${field}.view`),
    [hasBranchPermission]
  );

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/branches/trashed/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch trashed branch");
      }
      const { data } = await res.json();
      setBranches(data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error loading trashed branch",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchBranches();
  }, [canViewPage, fetchBranches, isAuthReady]);

  const handleRestoreClick = (event, branch) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!canRestore) {
      setSnackbar({ open: true, message: "You do not have permission to restore branches", color: "error" });
      return;
    }
    setSelectedBranch(branch);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!canRestore || !canConfirmRestore) {
      setSnackbar({ open: true, message: "You do not have permission to confirm restore", color: "error" });
      return;
    }

    try {
      setRestoringId(selectedBranch.id);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/branches/restore/${selectedBranch.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Restore failed");
      }
      setSnackbar({
        open: true,
        message: "Branch restored successfully",
        color: "success",
      });
      fetchBranches();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Restore failed",
        color: "error",
      });
    } finally {
      setRestoringId(null);
      setRestoreDialogOpen(false);
      setSelectedBranch(null);
    }
  };

  const closeRestoreDialog = () => {
    if (!canCancelRestore) return;
    setRestoreDialogOpen(false);
    setSelectedBranch(null);
  };

  const columns = [];
  if (canViewField("id")) columns.push({ Header: "ID", accessor: "id" });
  if (canViewField("name")) columns.push({ Header: "Name", accessor: "name" });
  if (canViewField("deleted_at")) columns.push({ Header: "Deleted At", accessor: "deleted_at" });
  if (canViewField("status")) columns.push({ Header: "Status", accessor: "status" });
  if (canViewField("actions") && canRestore) {
    columns.push({ Header: "Actions", accessor: "actions", disableSortBy: true });
  }

  const rows = branches.map((branch) => ({
    id: branch.id,
    name: branch.name,
    deleted_at: (
      <SoftTypography variant="body2" fontWeight="regular">
        {new Date(branch.deleted_at).toLocaleString()}
      </SoftTypography>
    ),
    status: (
      <SoftBox display="inline-flex" alignItems="center" px={1.5} py={0.5} borderRadius={1}>
        <SoftTypography variant="caption" color={branch.deleted_at || !branch.status ? "error" : "info"}>
          {branch.deleted_at || !branch.status ? "INACTIVE" : "ACTIVE"}
        </SoftTypography>
      </SoftBox>
    ),
    actions: (
      <>
        {canRestore && (
          <Tooltip title="Restore Branch">
            <IconButton
              size="small"
              color="success"
              onClick={(event) => handleRestoreClick(event, branch)}
              aria-label="restore"
              disabled={restoringId === branch.id}
            >
              {restoringId === branch.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RestoreIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </>
    ),
  }));

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="70vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Trashed Branch List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view trashed branches.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Branch Management"
        content={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Trashed Branch List
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/branches/add")}
              >
                Add New Branch
              </SoftButton>
            )}
            {canOpenList && (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>list</Icon>}
                onClick={() => navigate("/branches")}
              >
                Branch List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <DataTable
          table={{ columns, rows }}
          isSorted={true}
        />
      </SoftBox>

      <Dialog open={restoreDialogOpen} onClose={closeRestoreDialog}>
        <DialogTitle>Confirm Restore</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to restore the branch <strong>{selectedBranch?.name}</strong>?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          {canConfirmRestore && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleRestoreConfirm}
              disabled={restoringId === selectedBranch?.id}
              endIcon={restoringId === selectedBranch?.id ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {restoringId === selectedBranch?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}
          {canCancelRestore && (
            <SoftButton
              variant="contained"
              color="error"
              onClick={closeRestoreDialog}
            >
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default TrashedBranchList;
